import { PropsWithChildren } from 'react';

import { LoadingSpinner, Result, Status } from '@oaknorth/component-core-v2';
import { T } from '@oaknorth/util-intl';
import { useQuery } from '@oaknorth/util-rest';

import { AuthUserContextProvider } from './AuthUserContext';
import { TestUserContextProvider } from './TestUserContextProvider';
import { User } from './types';

const MOCK_USER_REQUEST_PATH = '/api/path/to/user-info';

async function fetchUserData(): Promise<User> {
  return fetch(MOCK_USER_REQUEST_PATH)
    .then((response) => response.json())
    .then((data) => data)
    .catch(() => {
      throw new Error();
    });
}

function CypressVRUserContextProvider({ children }: PropsWithChildren<{}>) {
  /**
   * This is a workaround to use Cypress with Auth0 in the VR tests.
   * The UserContext provider provides all components with the user information data.
   * Some pages that we test in cypress are only accessible to ONCI users,
   * so we cannot just use the TestUserContext provider with a hardcoded user object,
   * we need a way to set the user on a test-by-test basis.
   * We cannot use the real Auth0 provider to do a real login without setting up an Auth0 org for local dev.
   * The workaround is to call a fake endpoint that we can intercept and set the response on a case-by-case basis
   * to be the user that is needed for that test. Then we use the enpoint response to set the UserContext value.
   */
  // eslint-disable-next-line i18next/no-literal-string -- query key not user visible
  const getCypressUser = useQuery({ queryKey: ['get-cypress-user'], queryFn: fetchUserData });

  if (getCypressUser.isLoading) {
    return <LoadingSpinner />;
  }

  if (getCypressUser.error || !getCypressUser.data) {
    return <Result status={Status.ERROR} title={T`user.error.title`} />;
  }

  return (
    <TestUserContextProvider
      value={{
        user: getCypressUser.data,
        accessToken: '111111111111',
        onLogout: () => console.debug('Logout'),
      }}
    >
      {children}
    </TestUserContextProvider>
  );
}

export const CypressUserContextProvider =
  // @ts-expect-error -- we ensure Cypress is a field in window before accessing it
  'Cypress' in window && window.Cypress.env('test_mode') === 'visual_regression'
    ? CypressVRUserContextProvider
    : AuthUserContextProvider;
